import { BrowserRouter, Routes, Route, useNavigate, useLocation } from 'react-router-dom'
import { useCallback } from 'react'
import { Toaster } from 'react-hot-toast'
import { AppShell } from './components/shell'
import type { NavigationItem } from './components/shell'
import { BadgeNotificationSystem } from './components/sections/badges/BadgeNotificationSystem'
import { Discover, Submit, Alternatives, Community, Profile, About, SignIn, SignUp, EditWord, Word } from './pages'
import { AuthProvider, useAuth } from './contexts/AuthContext'
import { ErrorBoundary } from './components/ErrorBoundary'
import { usePageTracking } from './hooks'
import { trackSearch } from './lib/analytics'

function AppContent() {
  const navigate = useNavigate()
  const location = useLocation()
  const { user, signOut, loading } = useAuth()
  
  // Track page views automatically
  usePageTracking()

  const navigationItems: NavigationItem[] = [
    {
      label: 'كل الكلام',
      href: '/',
      isActive: location.pathname === '/' || location.pathname === '/discover',
    },
    {
      label: 'ضيف كلمة',
      href: '/submit',
      isActive: location.pathname === '/submit',
    },
    {
      label: 'الساحة الشعبية',
      href: '/community',
      isActive: location.pathname === '/community',
    },
    {
      label: 'عن كلامنتينا',
      href: '/about',
      isActive: location.pathname === '/about',
    },
  ]

  // Map Supabase user to AppShell user format
  const currentUser = user ? {
    id: user.id,
    name: user.user_metadata?.name || user.email?.split('@')[0] || 'User',
    avatarUrl: user.user_metadata?.avatar_url,
  } : undefined

  const handleNavigate = (href: string) => {
    console.log({href});
    navigate(href)
  }

  const handleLogout = async () => {
    try {
      await signOut()
      navigate('/')
    } catch (error) {
      console.error('Error logging out:', error)
    }
  }

  const handleSearch = useCallback((query: string) => {
    if (query) {
      trackSearch(query)
      navigate(`/?search=${encodeURIComponent(query)}`)
    } else if (location.pathname === '/' || location.pathname === '/discover') {
      navigate('/')
    }
  }, [navigate, location.pathname])

  const handleSubmitWord = () => {
    navigate('/submit')
  }

  const handleSignIn = () => {
    navigate('/signin')
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-900">
        <div className="text-slate-400">Loading...</div>
      </div>
    )
  }

  return (
    <AppShell
      navigationItems={navigationItems}
      user={currentUser}
      onNavigate={handleNavigate}
      onLogout={handleLogout}
      onSearch={handleSearch}
      onSubmitWord={handleSubmitWord}
      onSignIn={handleSignIn}
    >
      <Routes>
        <Route path="/" element={<Discover />} />
        <Route path="/discover" element={<Discover />} />
        <Route path="/submit" element={<Submit />} />
        <Route path="/word/:id" element={<Word />} />
        <Route path="/word/:id/alternatives" element={<Alternatives />} />
        <Route path="/word/:id/edit" element={<EditWord />} />
        <Route path="/community" element={<Community />} />
        <Route path="/profile" element={<Profile />} />
        <Route path="/profile/:userId" element={<Profile />} />
        <Route path="/about" element={<About />} />
      </Routes>
      
      {/* Global Badge Notification System */}
      {user && <BadgeNotificationSystem userId={user.id} />}
    </AppShell>
  )
}

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <BrowserRouter>
          <Toaster
            position="top-center"
            reverseOrder={false}
            toastOptions={{
              duration: 4000,
              style: {
                background: '#1e293b',
                color: '#f1f5f9',
                border: '1px solid #334155',
                padding: '16px',
                fontSize: '14px',
                fontFamily: 'Cairo, sans-serif',
              },
              success: {
                iconTheme: {
                  primary: '#f59e0b',
                  secondary: '#1e293b',
                },
              },
              error: {
                iconTheme: {
                  primary: '#f43f5e',
                  secondary: '#1e293b',
                },
              },
            }}
          />
          <Routes>
            {/* Auth routes (without AppShell) */}
            <Route path="/signin" element={<SignIn />} />
            <Route path="/signup" element={<SignUp />} />

            {/* Main app routes (with AppShell) */}
            <Route path="/*" element={<AppContent />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </ErrorBoundary>
  )
}

export default App
